import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DISABLED_KPI_CLICKS } from '@catalogues';
import { MilestoneName } from '@models';
import { SelectedKpiService } from '@services';
import { KpiCommonFunctionsService } from 'app/services/kpi-common-functions/kpi-common-functions.service';

import { Kpi, KpiKeys } from '../../../models/Kpi';

@Component({
  selector: 'kpi',
  templateUrl: './kpi.component.html',
  styleUrl: './kpi.component.scss'
})
export class KpiComponent implements OnInit, OnDestroy {
  private kpiCommonFunctionsService: KpiCommonFunctionsService = inject(KpiCommonFunctionsService);
  private selectedKpiService: SelectedKpiService = inject(SelectedKpiService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  @Input() kpi!: Kpi;
  @Input() kpiKey!: KpiKeys;
  @Input() selectionDisabled = false;
  @Input() keyMilestones?: MilestoneName[];
  @Input() width = 220;
  @Output() tooltipWasOpened = new EventEmitter<boolean>();

  displayValue?: number | string;
  displayUnit!: '%' | '';
  displaySecondaryValue?: string;
  isSelected!: boolean;

  // Tooltip state management
  tooltipOpen = false;
  private tooltipHideTimeoutId: number | null = null;
  private isKpiHovered = false;
  private isTooltipHovered = false;

  ngOnInit() {
    this.displayValue = this.kpiCommonFunctionsService.getKpiDisplayValue(this.kpi, this.kpiKey);
    this.displayUnit = this.kpiCommonFunctionsService.getKpiDisplayUnit(this.kpi.type);
    this.displaySecondaryValue = this.kpiCommonFunctionsService.getKpiDisplaySecondaryValue(
      this.kpi,
      this.kpiKey
    );

    if (!this.selectionDisabled) {
      this.selectedKpiService.selectedKpi$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((key) => {
          this.isSelected = key === this.kpiKey;
        });
    }
  }

  ngOnDestroy(): void {
    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
    }
  }

  onKpiMouseEnter(): void {
    this.isKpiHovered = true;

    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }

    this.tooltipOpen = true;
  }

  onKpiMouseLeave(): void {
    this.isKpiHovered = false;
    this.scheduleTooltipHide();
  }

  onTooltipHover(): void {
    this.isTooltipHovered = true;

    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }
  }

  onTooltipLeave(): void {
    this.isTooltipHovered = false;
    this.scheduleTooltipHide();
  }

  private scheduleTooltipHide(): void {
    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }

    // Don't hide if either KPI or tooltip is hovered
    if (this.isKpiHovered || this.isTooltipHovered) {
      return;
    }

    this.tooltipHideTimeoutId = window.setTimeout(() => {
      this.tooltipOpen = false;
      this.tooltipHideTimeoutId = null;
    }, 150);
  }

  handleTooltipOpened() {
    this.tooltipWasOpened.emit(true);
  }

  onKpiClick(event: Event) {
    event.stopPropagation(); // Prevent event bubbling
    // disable click for specific KPI keys if needed
    if (
      this.selectionDisabled ||
      DISABLED_KPI_CLICKS.includes(this.kpiKey) ||
      !this.displayValueValid
    )
      return;
    this.selectedKpiService.setSelectedKpi(this.isSelected ? undefined : this.kpiKey);
  }

  get displayValueValid(): boolean {
    return this.displayValue !== undefined; // Can be 0
  }
}
